/**
 * InclusivePricingCard Component
 *
 * Displays what's included in the base container rental price
 * in a positive, green-themed card. Shows container size, base price,
 * and all included services/features.
 */

import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../utils/formatCurrency';

export interface InclusivePricingCardProps {
  containerSize: string;
  materialType: string;
  basePrice: number;
  includedTonnage: number;
  rentalDays: number;
  includesDelivery: boolean;
}

export function InclusivePricingCard({
  containerSize,
  materialType,
  basePrice,
  includedTonnage,
  rentalDays,
  includesDelivery,
}: InclusivePricingCardProps) {
  const getMaterialLabel = (type: string): string => {
    const labels: Record<string, string> = {
      construction_debris: 'C&D',
      mixed_cd: 'Mixed C&D',
      concrete: 'Concrete',
      dirt: 'Dirt/Soil',
      general_waste: 'General Waste',
      green_waste: 'Green Waste',
    };
    return labels[type] || type;
  };

  return (
    <Card className="border-green-200 bg-green-50/30">
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl">
            {containerSize} Container Rental
          </CardTitle>
          <span className="text-2xl font-bold text-gray-900">
            {formatCurrency(basePrice)}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm font-medium text-gray-700 mb-3">
          What&apos;s included in your rental:
        </p>
        <div className="space-y-2">
          <InclusionItem>
            Up to {includedTonnage} tons of {getMaterialLabel(materialType)} disposal
          </InclusionItem>
          <InclusionItem>
            {rentalDays}-day rental period
          </InclusionItem>
          {includesDelivery && (
            <>
              <InclusionItem>Delivery to your location</InclusionItem>
              <InclusionItem>Pickup when you&apos;re done</InclusionItem>
              <InclusionItem>All transportation costs</InclusionItem>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InclusionItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <Check
        className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0"
        aria-hidden="true"
      />
      <span className="text-sm text-gray-700">{children}</span>
    </div>
  );
}
